/**
 * Models for the Post Reactions feature
 *
 * @remarks
 * Post reactions allow authenticated users to express a single emoji reaction
 * per blog post.  Only one reaction per user per post is allowed; submitting a
 * new type replaces (upserts) the previous one.
 *
 * NOTE: This module intentionally defines `PostReactionType` — not `ReactionType`
 * — to avoid a naming collision with the comment-level `ReactionType` already
 * exported from `base/references.model.ts` which covers a different reaction set
 * (like | dislike | love | laugh | angry | sad).
 *
 * @see POST_REACTIONS_ANGULAR_SERVICE.md for the full API reference
 */

// ---------------------------------------------------------------------------
// Reaction types
// ---------------------------------------------------------------------------

/**
 * The four reaction types supported on blog posts.
 *
 * Intentionally distinct from the comment-level `ReactionType` in
 * `base/references.model.ts`.
 */
export type PostReactionType = 'like' | 'love' | 'insightful' | 'funny';

/**
 * Ordered tuple of all valid PostReactionType values.
 * Useful for iterating reaction buttons.
 *
 * @example
 * ```typescript
 * @for (type of POST_REACTION_TYPES; track type) { ... }
 * ```
 */
export const POST_REACTION_TYPES: readonly PostReactionType[] = [
  'like',
  'love',
  'insightful',
  'funny'
] as const;

// ---------------------------------------------------------------------------
// Count / summary models
// ---------------------------------------------------------------------------

/**
 * Reaction count breakdown for a single post
 */
export interface PostReactionCounts {
  like: number;
  love: number;
  insightful: number;
  funny: number;
}

/**
 * Response body for GET /api/posts/:postId/reactions
 */
export interface PostReactionsResponse {
  success: boolean;
  data: {
    counts: PostReactionCounts;
    /** Null when not authenticated or when the user has not reacted */
    userReaction: PostReactionType | null;
  };
}

// ---------------------------------------------------------------------------
// Request / response shapes
// ---------------------------------------------------------------------------

/**
 * Request body for POST /api/posts/:postId/reactions
 */
export interface UpsertReactionPayload {
  orgId: string;
  reactionType: PostReactionType;
}

/**
 * Standard action response for upsert and delete reaction endpoints
 */
export interface ReactionActionResponse {
  success: boolean;
  message: string;
}

// ---------------------------------------------------------------------------
// Signal state type
// ---------------------------------------------------------------------------

/**
 * Per-post reactive state held by PostReactionService
 */
export interface PostReactionEntry {
  counts: PostReactionCounts;
  /** Null indicates no reaction from the current user */
  userReaction: PostReactionType | null;
}
